import React, { useMemo, useRef, useState, useCallback, useEffect } from 'react'
import { ResponsiveHeatMapCanvas } from '@nivo/heatmap'
import { throttle } from 'lodash'
import Tippy from '@tippyjs/react'
import { followCursor } from 'tippy.js'
import { HeatmapTooltip } from './HeatmapTooltip'


function CipCard({
  cipItem,
  nontrad,
  rows,
  groupMaxPercents,
  leftMargin,
  topMargin,
  containerWidth,
  containerHeight,
  onHeaderClick,
}) {

  const [hoveredContent, setHoveredContent] = useState(null)
  const containerRef = useRef(null)
  const cellsRef = useRef([])

  const combinedRows = rows

  const isNonTrad =
    nontrad.female.includes(cipItem.cipcode) ||
    nontrad.male.includes(cipItem.cipcode)

  // ----------------------------------------------------------------
  // 1) Pattern Background Layer
  // ----------------------------------------------------------------
  const PatternBackgroundLayer = (ctx) => {
    const canvasWidth = ctx.canvas.width
    const canvasHeight = ctx.canvas.height
    const patternCanvas = document.createElement('canvas')
    patternCanvas.width = 8
    patternCanvas.height = 8
    const pctx = patternCanvas.getContext('2d')

    pctx.fillStyle = '#ffffff'
    pctx.fillRect(0, 0, 8, 8)

    pctx.strokeStyle = '#ccc'
    pctx.lineWidth = 0.5
    pctx.beginPath()
    pctx.moveTo(0, 0)
    pctx.lineTo(8, 8)
    pctx.stroke()

    const diagonalPattern = ctx.createPattern(patternCanvas, 'repeat')
    ctx.save()
    ctx.fillStyle = diagonalPattern
    ctx.fillRect(0, 0, canvasWidth, canvasHeight)
    ctx.restore()
  }

  // ----------------------------------------------------------------
  // 2) Custom Labels Layer
  // ----------------------------------------------------------------
  const CustomLabelsLayer = (ctx, { cells, margin }) => {
    const m = margin || { left: 0, top: 0 }
    ctx.save()
    ctx.translate(m.left, m.top)

    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.font = '400 14px sans-serif'

    cells.forEach((cell) => {
      if (!isNaN(cell.value) && cell.value !== null) {
        const { x, y, formattedValue } = cell
        ctx.fillStyle = '#ffffff'
        ctx.fillText(formattedValue, x, y)
      }
    })

    ctx.restore()
  }

  // ----------------------------------------------------------------
  // 3) DebugHitboxesLayer: store cell coords for hit-testing
  // ----------------------------------------------------------------
  function DebugHitboxesLayer(ctx, { cells }) {
    const pxRatio = 2
    const offsetX = leftMargin * pxRatio
    const offsetY = topMargin * pxRatio
    ctx.save()

    cellsRef.current = cells.map(cell => {
      const dx = (cell.x - cell.width / 2) * pxRatio + offsetX
      const dy = (cell.y - cell.height / 2) * pxRatio + offsetY
      const dw = cell.width * pxRatio
      const dh = cell.height * pxRatio

      return { ...cell, deviceX: dx, deviceY: dy, deviceW: dw, deviceH: dh }
    })

    ctx.restore()
  }

  // ----------------------------------------------------------------
  // 4) SpacerCellsLayer: draw white boxes for spacer rows
  // ----------------------------------------------------------------
  function SpacerCellsLayer(ctx, { cells, margin, pixelRatio }) {
    ctx.save()

    if (pixelRatio) {
      ctx.scale(pixelRatio, pixelRatio)
    }
    if (margin) {
      ctx.translate(margin.left, margin.top)
    }

    cells.forEach(cell => {
      if (cell.serieId === 'spacer') {
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(
          cell.x - (cell.width / 2) - 50,
          cell.y - cell.height / 2,
          cell.width + 50,
          cell.height
        )
      }
    })

    ctx.restore()
  }

  // ----------------------------------------------------------------
  // 5) Throttled Mouse Move & Hit Testing for tooltip
  // ----------------------------------------------------------------
  const handleMouseMove = useCallback((e) => {
    const canvas = containerRef.current?.querySelector('canvas');
    if (!canvas) {
      setHoveredContent(null);
      return;
    }
    const rect = canvas.getBoundingClientRect();
    const relX = (e.clientX - rect.left) * (canvas.width / rect.width);
    const relY = (e.clientY - rect.top)  * (canvas.height / rect.height);

    let foundCell = null;
    for (const cell of cellsRef.current) {
      const { deviceX, deviceY, deviceW, deviceH } = cell;
      if (
        relX >= deviceX &&
        relX <= deviceX + deviceW &&
        relY >= deviceY &&
        relY <= deviceY + deviceH
      ) {
        foundCell = cell;
        break;
      }
    }

    if (!foundCell) {
      setHoveredContent(null);
      return;
    }

    const { serieId } = foundCell;
    if (serieId === "spacer") {
      setHoveredContent(null);
      return;
    }

    const { denominator } = foundCell.data ?? {};
    if (!denominator || denominator <= 0) {
      setHoveredContent(null);
      return;
    }

    const hoveredGroupMaxPercent = groupMaxPercents[serieId] ?? 0;

    setHoveredContent(
      <HeatmapTooltip
        cell={foundCell}
        maxPercent={hoveredGroupMaxPercent}
      />
    );
  }, [groupMaxPercents]);

  const handleMouseMoveThrottled = useMemo(
    () => throttle(handleMouseMove, 16),
    [handleMouseMove]
  );

  // This handler will be called when the mouse leaves the canvas container.
  const handleMouseLeave = useCallback(() => {
    setHoveredContent(null);
  }, []);

  // ----------------------------------------------------------------
  // 6) Global mousemove listener as a backup for browsers (like Edge)
  // ----------------------------------------------------------------
  useEffect(() => {
    const handleWindowMouseMove = (e) => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        // If the pointer is outside the container, hide the tooltip.
        if (
          e.clientX < rect.left ||
          e.clientX > rect.right ||
          e.clientY < rect.top ||
          e.clientY > rect.bottom
        ) {
          setHoveredContent(null);
        }
      }
    };

    window.addEventListener('mousemove', handleWindowMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleWindowMouseMove);
    };
  }, []);

  // ----------------------------------------------------------------
  // 7) Nivo Heatmap Props
  // ----------------------------------------------------------------
  const heatmapProps = useMemo(
    () => ({
      margin: { top: topMargin, left: leftMargin },
      pixelRatio: 2,
      colors: {
        type: 'diverging',
        scheme: 'red_blue',
        minValue: -0.2,
        maxValue: 0.2,
        divergeAt: 0.5,
      },
      emptyColor: 'transparent',
      borderRadius: 0,
      borderWidth: 1,
      borderColor: '#FFFFFF',
      enableLabels: false,
      isInteractive: false,
      animate: false,
      label: (d) =>
        !isNaN(d.value) && d.value ? d.formattedValue : '',
      valueFormat: (v) => `${(Number(v) * 100).toFixed(0)}`,
    }),
    [topMargin, leftMargin]
  )

  const labelAliases = {
    'American Indian/Alaskan Native': 'Am. Indian/AK Native',
    'Native Hawaiian/Other Pacific Islander': 'Hawaiian/Pac. Isl.',
    'Two or More Races': '2+ Races',
    'Black/African American': 'Black/Afr. Am.',
    'Economically Disadvantaged': 'Econ. Disadvantaged',
    'Students w/ Disabilities': 'Students w/ Disab.',
  }
  const aliasOrSelf = (value) => labelAliases[value] || value

  // ----------------------------------------------------------------
  // Render
  // ----------------------------------------------------------------
  return (
    <div className="h-full min-h-[100px] flex flex-row mb-[50px] mx-3 cursor-pointer" onClick={() => onHeaderClick?.(cipItem.cipcode)}>
      <div className="bg-white rounded-xl group relative hover:border-sky-100 shadow-lg shadow-neutral-100 border-[4px] border-slate-100">
        <button className="flex w-[360px] h-[72px]">
          {isNonTrad && (
            <div className="absolute -top-3 -left-2 w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600">
              <span className="text-xs font-bold">NT</span>
            </div>
          )}
          <div className="inline-flex group ml-4 mt-4 items-top">
            <div className="w-[5px] h-[18px] mx-2 mt-1 bg-[#3C43FF]" />
            <p className="text-left text-ellipsis overflow-hidden w-[250px] whitespace-pre text-slate-900">
              {`${cipItem.cipcodeName}
${cipItem.cipcodeName !== cipItem.cipcode ? cipItem.cipcode : ''} ${
                nontrad.female.includes(cipItem.cipcode)
                  ? '(Non Traditional: Female)'
                  : nontrad.male.includes(cipItem.cipcode)
                  ? '(Non Traditional: Male)'
                  : ''
              }`}
            </p>
          </div>
        </button>

        <Tippy
          content={hoveredContent}
          visible={!!hoveredContent}
          followCursor={true}
          theme="clusterChartBlue"
          plugins={[followCursor]}
          arrow={false}
          offset={[5, 15]}
          appendTo={document.body}
          placement="bottom-start"
          popperOptions={{
            modifiers: [
              { name: 'flip', enabled: false },
              { name: 'preventOverflow', options: { padding: 20 } },
            ],
          }}
        >
          <div />
        </Tippy>

        <div
          ref={containerRef}
          onMouseMove={handleMouseMoveThrottled}
          onMouseLeave={handleMouseLeave}
          className="my-5 mr-5"
          style={{ width: containerWidth, height: containerHeight }}
        >
          <ResponsiveHeatMapCanvas
            {...heatmapProps}
            data={combinedRows}
            axisTop={combinedRows.length > 0}
            axisLeft={{
              format: aliasOrSelf,
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
            }}
            layers={[
              PatternBackgroundLayer,
              'grid',
              'axes',
              'cells',
              DebugHitboxesLayer,
              SpacerCellsLayer,
              CustomLabelsLayer,
            ]}
          />
        </div>
      </div>
    </div>
  )
}

export default React.memo(CipCard)